import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowDownToLineIcon } from 'lucide-react';
import { ViewToggle, type ViewMode } from './components/ViewToggle';
import { PortfolioView } from './pages/PortfolioView';
import { CvView } from './pages/CvView';
import { SectionNav } from './components/portfolio/SectionNav';
import cvData from './data/cv';
export function App() {
  const [view, setView] = useState<ViewMode>('interactive');
  const reduce = useReducedMotion();
  const {
    profile,
    meta
  } = cvData;
  const dark = view === 'interactive';
  // Abbreviated name for the on-screen site (full name stays on the printed CV).
  const [firstName, ...restName] = profile.name.split(' ');
  const shortName = restName.length ? `${firstName} ${restName.join(' ').charAt(0)}.` : firstName;
  const handlePrint = () => {
    const prevTitle = document.title;
    document.title = `${profile.name.replace(/\s+/g, '-')}-${meta.exportFileSuffix}`;
    window.print();
    document.title = prevTitle;
  };
  return <div className="print-root relative min-h-screen w-full transition-colors duration-500" style={{
    background: dark ? 'var(--pf-bg)' : '#efe9e0'
  }} data-id="element-0" data-mp="3ihv2gfixenje">
      {/* Persistent header — carries the animated toggle across both views. */}
      <header className={`no-print fixed inset-x-0 top-0 z-[60] transition-colors duration-500 ${dark ? 'border-b border-white/10 bg-[var(--pf-bg)]/80 text-white' : 'border-b border-[var(--line-strong)] bg-[#efe9e0]/90 text-[var(--ink)]'} backdrop-blur-md`} data-id="element-1" data-mp="4wyzymxdckh">
        <div className="mx-auto flex max-w-6xl items-center justify-center gap-3 px-4 py-3 sm:justify-between sm:px-6" data-id="element-2" data-mp="38l8gfnev5qfs">
          <div className="hidden min-w-0 flex-col leading-none sm:flex" data-id="element-3" data-mp="3dlw4q1bpjt6b">
            <span className={`truncate font-display text-[15px] italic ${dark ? 'text-white' : 'text-[var(--ink)]'}`} data-id="element-4" data-mp="nw2bc3tnpmq1">
              {shortName}
            </span>
            <span className={`mt-1 truncate font-mono text-[8.5px] uppercase tracking-[0.18em] ${dark ? 'text-stone-500' : 'text-[var(--ink-faint)]'}`} data-id="element-5" data-mp="3684vetzoctf">
              {cvData.meta.kicker}
            </span>
          </div>

          <div className="flex items-center gap-2.5" data-id="element-6" data-mp="d45tw4joadn3">
            {view === 'cv' && <button type="button" onClick={handlePrint} className="group inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-3.5 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--paper)] transition-colors hover:bg-[var(--accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]" data-id="element-7" data-mp="86w58r43y60b">
                <ArrowDownToLineIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" aria-hidden="true" data-id="element-8" data-mp="3liwjibgolmo8" />
                <span className="hidden sm:inline" data-id="element-9" data-mp="2suqzjqxgc1pg">Export PDF</span>
                <span className="sm:hidden" data-id="element-10" data-mp="18e1axny9tulm">PDF</span>
              </button>}

            <ViewToggle view={view} onChange={setView} theme={dark ? 'dark' : 'light'} data-id="element-11" data-mp="1d4hp5a9x1k91" />
          </div>
        </div>
      </header>

      {/* Section rail — rendered OUTSIDE the transformed view wrapper so its
            position:fixed anchors to the viewport (a transformed ancestor would
            otherwise capture it). Interactive view only. */}
      {view === 'interactive' && <SectionNav data-id="element-12" data-mp="1w3lry1av6hs9" />}

      {/* Seamless cross-fade between the two surfaces. */}
      <AnimatePresence mode="wait" data-id="element-13" data-mp="1i4401d29uoeo">
        <motion.div key={view} initial={{
        opacity: 0,
        y: reduce ? 0 : 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: reduce ? 0 : -12
      }} transition={{
        duration: reduce ? 0 : 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} className="pt-[52px]" data-id="element-14" data-mp="1i31b3aoiew1e">
          {view === 'interactive' ? <PortfolioView onViewCv={() => setView('cv')} data-id="element-15" data-mp="m5ng5cke0rqk" /> : <CvView data-id="element-16" data-mp="h6klmm21a3wu" />}
        </motion.div>
      </AnimatePresence>
    </div>;
}